import { TemplatePresentationItemDisplay } from 'types/dataHelios';

import type { FilterCondition } from 'types/tableFilters';
import type { ComparisonOperator, HeliosRecordDataTablesColumn } from 'types/dataHelios';
import { resolveStaticSelectionValues } from 'utils/selectItemLookup';

interface SelectItem {
  key: string;
  value: string;
}

type TranslationFn = (key: string) => string;

interface ParsedBrowseValue {
  name: string;
}

const isParsedBrowseValue = (value: unknown): value is ParsedBrowseValue => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  if (!('name' in value)) {
    return false;
  }

  return typeof value.name === 'string';
};

export const getOperatorDisplayName = (operator: ComparisonOperator, t: TranslationFn): string =>
  t(`helios.templates.conditions.condition.comparisons.${String(operator)}`);

const parseBrowseName = (value: string): string | null => {
  if (!value) {
    return null;
  }

  try {
    const parsedValue: unknown = JSON.parse(value);
    return isParsedBrowseValue(parsedValue) ? parsedValue.name : null;
  } catch {
    return null;
  }
};

const hasBlankState = (isBlank: FilterCondition['isBlank']): isBlank is boolean => isBlank !== null && isBlank !== undefined;

export const getStaticSelectionDisplayValue = (value: string, selectItems: SelectItem[]): string => {
  const foundItem = selectItems.find((item) => item.key === value);
  return foundItem ? foundItem.value : value;
};

export const getBrowseValueDisplay = (value: string): string => {
  return parseBrowseName(value) || '';
};

export const getFormValueDisplay = (value: string, selectedColumn: HeliosRecordDataTablesColumn | undefined): string => {
  if (selectedColumn?.displayType === TemplatePresentationItemDisplay.Browse) {
    return getBrowseValueDisplay(value);
  }

  return value;
};

export const getFilterValueDisplay = (filter: FilterCondition, columns: HeliosRecordDataTablesColumn[], t: TranslationFn): string => {
  if (hasBlankState(filter.isBlank)) {
    return filter.isBlank ? t('helios.templates.filters.isBlankEmpty') : t('helios.templates.filters.isBlankNotEmpty');
  }

  if (filter.staticValues && filter.staticValues.length > 0) {
    const column = columns.find((col) => col.columnKey === filter.columnKey);
    const selectItems = column?.settings?.staticSelection?.selectItems;
    return selectItems ? resolveStaticSelectionValues(filter.staticValues, selectItems) : filter.staticValues.join(', ');
  }

  if (filter.fromValue || filter.toValue) {
    const fromPart = filter.fromValue ? filter.fromValue : '...';
    const toPart = filter.toValue ? filter.toValue : '...';
    return `${fromPart} – ${toPart}`;
  }

  if (filter.displayType === TemplatePresentationItemDisplay.StaticSelection) {
    const column = columns.find((col) => col.columnKey === filter.columnKey);
    const selectItems = column?.settings?.staticSelection?.selectItems;
    return selectItems ? getStaticSelectionDisplayValue(filter.value, selectItems) : filter.value;
  }

  if (filter.displayType === TemplatePresentationItemDisplay.Browse) {
    if (!filter.value) {
      return t('common.empty');
    }

    return parseBrowseName(filter.value) || filter.value;
  }

  return filter.value || t('common.empty');
};

export const getFilterRowDisplay = (filter: FilterCondition, columns: HeliosRecordDataTablesColumn[], t: TranslationFn): string => {
  if (hasBlankState(filter.isBlank)) {
    const state = filter.isBlank ? t('helios.templates.filters.isBlankEmpty') : t('helios.templates.filters.isBlankNotEmpty');
    return `${filter.columnName} ${state}`;
  }

  return `${filter.columnName} ${getOperatorDisplayName(filter.operator, t)} ${getFilterValueDisplay(filter, columns, t)}`;
};
